/**
 * IndustryPacksSection
 *
 * Per-tenant industry-pack install surface mounted on TenantDetailPage. Lists
 * every pack registered in the backend (`GET /platform-admin/industry-packs`)
 * as a card with Norwegian display name, description, version, and content
 * counts. The "Forhåndsvis" button on each card opens IndustryPackPreviewModal
 * to dry-run + commit a pack install via the existing template-seeding
 * endpoint.
 *
 * Per-pack "Installert <date> av <operator>" derives from the platform audit
 * log: we query INDUSTRY_PACK_SEED entries for this tenant and pick the most
 * recent non-dry-run match per `details.source`. Master tenants are filtered
 * out as a UI defense-in-depth — the server already rejects pack installs
 * targeting `slug: 'master'` (Phase C-1 task 4.4).
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  industryPackService,
  type IndustryPackListItem,
} from '../../services/industryPackService';
import { auditService } from '../../services/auditService';
import { IndustryPackPreviewModal } from './IndustryPackPreviewModal';
import { SeedAuditEntry } from './SeedAuditEntry';
import type { PlatformAuditLog } from '../../types';

interface IndustryPacksSectionProps {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  environment?: string;
}

interface InstallSummary {
  performedAt: string;
  performer: string;
}

const formatPerformer = (entry: PlatformAuditLog): string => {
  const { firstName, lastName, email } = entry.user;
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  return name.length > 0 ? name : email;
};

const formatPerformedAt = (iso: string): string => {
  try {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return 'Invalid date';
    return format(date, 'dd.MM.yyyy HH:mm');
  } catch {
    return 'Invalid date';
  }
};

const buildInstallByPackKey = (
  entries: PlatformAuditLog[]
): Record<string, InstallSummary> => {
  const result: Record<string, InstallSummary> = {};
  for (const entry of entries) {
    const details =
      entry.details && typeof entry.details === 'object'
        ? (entry.details as Record<string, unknown>)
        : null;
    if (!details) continue;
    if (details.dryRun === true) continue;
    const source = details.source;
    if (typeof source !== 'string' || !source.startsWith('pack:')) continue;
    const packKey = source.slice('pack:'.length);
    if (!packKey) continue;
    const existing = result[packKey];
    if (
      existing &&
      new Date(existing.performedAt).getTime() >=
        new Date(entry.performedAt).getTime()
    ) {
      continue;
    }
    result[packKey] = {
      performedAt: entry.performedAt,
      performer: formatPerformer(entry),
    };
  }
  return result;
};

/**
 * Build the list of content-count chips for a pack card. Only types with a
 * non-zero count surface; types omitted from the pack stay invisible so the
 * card stays scannable.
 */
const buildContentChips = (pack: IndustryPackListItem): string[] => {
  const chips: string[] = [];
  if (pack.contentCounts.checklistGroups > 0) {
    chips.push(`${pack.contentCounts.checklistGroups} sjekklister`);
  }
  if (pack.contentCounts.folderTemplates > 0) {
    chips.push(`${pack.contentCounts.folderTemplates} mappemaler`);
  }
  if (pack.contentCounts.activityTypes > 0) {
    chips.push(`${pack.contentCounts.activityTypes} aktivitetstyper`);
  }
  if (pack.contentCounts.consentBases > 0) {
    chips.push(`${pack.contentCounts.consentBases} samtykkegrunnlag`);
  }
  return chips;
};

export function IndustryPacksSection({
  tenantId,
  tenantName,
  tenantSlug,
  environment = 'production',
}: IndustryPacksSectionProps) {
  const [selectedPack, setSelectedPack] = useState<IndustryPackListItem | null>(
    null
  );

  const isMasterTenant = tenantSlug === 'master';

  const availablePacksQuery = useQuery({
    queryKey: ['industry-packs'],
    queryFn: () => industryPackService.getAvailablePacks(),
    enabled: !isMasterTenant,
  });

  // Single query for the section's two audit-derived surfaces:
  //   - Per-pack "Installert <date> av <operator>" indicator (filters this
  //     query result to INDUSTRY_PACK_SEED entries, picks newest per pack).
  //   - "Installeringshistorikk" accordion below the pack cards (renders the
  //     full audit list with the per-entity-type expand-toggle).
  // We fetch master + pack streams together so an operator who opens the
  // accordion immediately gets both, not just pack installs.
  const seedHistoryQuery = useQuery({
    queryKey: ['platform', 'audit-logs', 'seed-history', tenantId],
    queryFn: () => auditService.getSeedAuditEntries(tenantId),
    enabled: !isMasterTenant,
  });

  const installByPackKey = useMemo<Record<string, InstallSummary>>(() => {
    const entries = seedHistoryQuery.data ?? [];
    return buildInstallByPackKey(entries);
  }, [seedHistoryQuery.data]);

  const seedHistoryEntries = seedHistoryQuery.data ?? [];

  const packs = availablePacksQuery.data ?? [];

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Industri-pakker
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          Installer bransje-spesifikke sjekklister og mappestrukturer på
          tenanten.
        </Typography>

        {isMasterTenant && (
          <Alert severity="info">
            Industri-pakker kan ikke installeres på master-tenanten
            (sikkerhetsregel).
          </Alert>
        )}

        {!isMasterTenant && availablePacksQuery.isLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 2,
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Laster pakker…
            </Typography>
          </Box>
        )}

        {!isMasterTenant && availablePacksQuery.isError && (
          <Alert severity="error">Kunne ikke laste industri-pakker.</Alert>
        )}

        {!isMasterTenant &&
          !availablePacksQuery.isLoading &&
          !availablePacksQuery.isError &&
          packs.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 2 }}
            >
              Ingen industri-pakker er registrert.
            </Typography>
          )}

        {!isMasterTenant && packs.length > 0 && (
          <Stack spacing={2}>
            {packs.map((pack) => {
              const chips = buildContentChips(pack);
              const install = installByPackKey[pack.key];
              return (
                <Box
                  key={pack.key}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                  }}
                  data-testid={`pack-card-${pack.key}`}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 240 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ flexWrap: 'wrap', mb: 0.5 }}
                      >
                        <Typography variant="h6" component="div">
                          {pack.displayName}
                        </Typography>
                        <Chip
                          label={`v${pack.version}`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {pack.descriptionNo}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flexWrap: 'wrap', rowGap: 1 }}
                      >
                        {chips.map((label) => (
                          <Chip
                            key={label}
                            label={label}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 1,
                        minWidth: 200,
                      }}
                    >
                      <Button
                        variant="contained"
                        startIcon={<PreviewIcon />}
                        onClick={() => setSelectedPack(pack)}
                        sx={{ minHeight: 48 }}
                      >
                        Forhåndsvis
                      </Button>
                      <Typography
                        variant="caption"
                        color={install ? 'success.main' : 'text.secondary'}
                        sx={{ textAlign: 'right' }}
                      >
                        {install
                          ? `Installert ${formatPerformedAt(install.performedAt)} av ${install.performer}`
                          : 'Ikke installert'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}

        {!isMasterTenant && !seedHistoryQuery.isLoading && (
          <Accordion
            elevation={0}
            disableGutters
            square
            sx={{
              mt: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Installeringshistorikk ({seedHistoryEntries.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {seedHistoryEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Ingen seed-hendelser registrert for denne tenanten.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {seedHistoryEntries.map((entry) => (
                    <SeedAuditEntry key={entry.id} entry={entry} />
                  ))}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>

      {selectedPack && (
        <IndustryPackPreviewModal
          open={!!selectedPack}
          pack={selectedPack}
          tenantId={tenantId}
          tenantName={tenantName}
          environment={environment}
          onClose={() => setSelectedPack(null)}
          onInstalled={() => {
            void seedHistoryQuery.refetch();
            setSelectedPack(null);
          }}
        />
      )}
    </Card>
  );
}
